import { RelodeFormikBaseProps, useRelodeFormik } from "@relode/core";
import { IClientProfile } from "../../../shared/types/profile/IClientProfile";
import { Formik } from "formik";
import * as React from "react";

import { createContactSchema } from "./contactSchema";
import { useSettingsActions } from "./settingsActions";
import { useSettingsHandler } from "./settingsHandler";
import { settingsReducer } from "./settingsReducer";
import { initialState, SettingsState } from "./settingsState";

const SettingsContext = React.createContext<
    {
        contact: IClientProfile;
        state: SettingsState;
        actions: ReturnType<typeof useSettingsActions>;
    } & RelodeFormikBaseProps<IClientProfile>
>(undefined);

export const SettingsProvider = (props: { children: React.ReactNode }) => {
    const [state, dispatch] = React.useReducer(settingsReducer, initialState);
    const actions = useSettingsActions(dispatch);

    useSettingsHandler(state, actions);

    const {
        values: contact,
        formikProps,
        setFieldTouched,
        setFieldValue,
        touchErrors,
    } = useRelodeFormik<IClientProfile>();
    const value = {
        contact,
        state,
        actions,
        formikProps,
        setFieldTouched,
        setFieldValue,
        touchErrors,
    };

    React.useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        actions.fetchSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <SettingsContext.Provider value={value}>
            {props.children}
        </SettingsContext.Provider>
    );
};

export const useSettingsContext = () => {
    const context = React.useContext(SettingsContext);
    if (!context) {
        throw new Error(
            "useSettingsContext must be used within a SettingsProvider"
        );
    }

    return context;
};

export const SettingsForm = (props: { children: React.ReactNode }) => (
    <Formik
        initialValues={null as IClientProfile}
        onSubmit={() => {
            /* noop */
        }}
        enableReinitialize
        validateOnBlur={false}
        validateOnChange={true}
        validationSchema={createContactSchema}
    >
        <SettingsProvider>{props.children}</SettingsProvider>
    </Formik>
);
